import * as React from "react";
import { Grid, Typography, Divider, Paper, Box } from "@mui/material";
import { useGetRentalTax, usePropertyDashboardRevenueMonths } from "services/hooks/rentals";
import { currencyFormatter } from "utilities/currencyFormatter";
import { useSelector } from "react-redux";
import BarChart from "components/Graphs/ReusableBarChart";
import { FlexColLeft } from "components/FlexBox/FlexBox";
import { MdBalance } from "react-icons/md";
import { GiCash } from "react-icons/gi";
import { BsCart4 } from "react-icons/bs";
import moment from "moment";
import DashboardFilter from "components/Filters/dashBoardFilter";

export default function RentalTax() {
    const landlordId = useSelector((state) => state.landlord);
    const years = useSelector((state) => state.years);
    const [selectedYear, setSelectedYear] = React.useState(moment().format("YYYY"));
    const [selectedMonth, setSelectedMonth] = React.useState(moment().format("MM"));
    
    const { rentalTaxDetails, isLoadingRentalTaxDetails, errorRentalTaxDetails } = useGetRentalTax(landlordId, selectedYear);
    const { propertyDashboardRevenueMonths, isLoadingPropertyDashboardRevenueMonths, errorPropertyDashboardRevenueMonths } = usePropertyDashboardRevenueMonths(landlordId, 'landlord_id', selectedYear);
    
    const handleYearFilterChange = (year) => {
        setSelectedYear(year);
    };

    const handleMonthFilterChange = (month) => {
        setSelectedMonth(month);
    };
    
    // Extract data from the API response
    const total_revenue = rentalTaxDetails?.total_revenue || 0;
    console.log("total_revenue",rentalTaxDetails);
    const sole = rentalTaxDetails?.sole || 0;
    const partnerships = rentalTaxDetails?.partnerships || 0;
    const companies = rentalTaxDetails?.companies || 0;
    const months = propertyDashboardRevenueMonths?.months || [];

    // Calculate total income and expenses for the summary cards
    const totalIncome = months.reduce((sum, month) => sum + (month.income || 0), 0);
    const totalExpense = months.reduce((sum, month) => sum + (month.expense || 0), 0);
    const totalBalance = totalIncome - totalExpense;

    // Calculate tax for Individuals (using totalIncome)
    // 1. Subtract UGX 2,820,000 from total income
    // 2. Get 12% of the taxable income
    const taxableIncomeForIndividuals = Math.max(0, totalIncome - 2820000);
    const calculatedSole = taxableIncomeForIndividuals * 0.12;

    // Calculate tax for Businesses (using totalBalance)
    // 1. URA allows 50% of annual gross rental income as expenses
    // 2. Taxable income = 50% of total balance
    // 3. Get 30% of the taxable income
    const taxableIncomeForBusinesses = totalBalance * 0.5;
    const calculatedPartnerships = taxableIncomeForBusinesses * 0.30;

    // Prepare data for the bar chart
    const revenueMonthsDataSet = months.map(month => ({
        ...month,
        alias: month.alias || month.month.substring(0, 3)
    }));

    return (
        <>
            <Box sx={{ marginBottom: "15px", display: { xs: "block", sm: "flex", md: "flex" }, alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 500 }} variant="h3" component="div">
                    Revenue report for the year {selectedYear}
                </Typography>
                <DashboardFilter 
                    years={years} 
                    handleYearFilterChange={handleYearFilterChange} 
                    handleMonthFilterChange={handleMonthFilterChange}
                    selectedYear={selectedYear} 
                    selectedMonth={selectedMonth}
                />
            </Box>
            
            <Grid container spacing={2}>
                {/* Revenue Overview Section */}
                <Grid item xs={12}>
                    <Paper sx={{ padding: "1rem", width: "100%" }}>
                        <Grid container xs={12}>
                            <Grid item xs={12} sx={{ margin: "1rem" }}>
                                <Typography variant="h6" fontWeight="bold">Revenue Overview</Typography>
                                <Typography variant="body2">
                                    Your finance report for all properties for year {selectedYear}
                                </Typography>
                                <Divider />
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8} sx={{ padding: "0.5rem" }}>
                                    <BarChart
                                        label={revenueMonthsDataSet.map(x => x.alias)}
                                        data={[
                                            {
                                                label: "Income",
                                                data: revenueMonthsDataSet.map(x => x.income || 0),
                                                fill: false,
                                                backgroundColor: "rgba(135, 211, 124)",
                                                tension: 0.1,
                                            },
                                            {
                                                label: "Expense",
                                                data: revenueMonthsDataSet.map(x => x.expense || 0),
                                                fill: false,
                                                backgroundColor: "rgba(30, 139, 195)",
                                                tension: 0.1,
                                            },
                                        ]}
                                    />
                                </Grid>
                                <Divider orientation="vertical" variant="middle" className="desktop-only" />
                                <Grid item xs={12} md={3.7} sx={{ padding: "0.5rem" }}>
                                    <FlexColLeft sx={{ display: "flex", flexDirection: "column", padding: "0.5rem" }}>
                                        <Box sx={{ margin: "1rem", display: "flex" }}>
                                            <Typography sx={{ color: "#A05ACE", padding: "5px 5px 0 5px", borderRadius: "12px", marginRight: "1px", backgroundColor: "#ffebff" }} variant="h2">
                                                {<MdBalance />}
                                            </Typography>
                                            <div>
                                                <Typography variant="h5" fontWeight="bold">
                                                    {currencyFormatter(parseFloat(totalBalance))}
                                                </Typography>
                                                <Typography variant="body2">Total Balance</Typography>
                                            </div>
                                        </Box>
                                        <Box sx={{ margin: "1rem", display: "flex" }}>
                                            <Typography sx={{ color: "green", padding: "5px 5px 0 5px", borderRadius: "12px", marginRight: "1px", backgroundColor: "#B5E550" }} variant="h2">
                                                {<GiCash />}
                                            </Typography>
                                            <div>
                                                <Typography variant="h5" fontWeight="bold">
                                                    {currencyFormatter(parseFloat(totalIncome))}
                                                </Typography>
                                                <Typography variant="body2">Total Income</Typography>
                                            </div>
                                        </Box>
                                        <Box sx={{ margin: "1rem", display: "flex" }}>
                                            <Typography sx={{ color: "rgba(30, 139, 195)", padding: "5px 5px 0 5px", borderRadius: "12px", marginRight: "1px", backgroundColor: "lightblue" }} variant="h2">
                                                {<BsCart4 />}
                                            </Typography>
                                            <div>
                                                <Typography variant="h5" fontWeight="bold">
                                                    {currencyFormatter(parseFloat(totalExpense))}
                                                </Typography>
                                                <Typography variant="body2">Total Expense</Typography>
                                            </div>
                                        </Box>
                                    </FlexColLeft>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Tax Calculations Section */}
                <Grid item xs={12}>
                    <Paper sx={{ padding: "2rem", mt: 2 }}>
                        <Typography variant="h5" fontWeight="bold" mb={2}>
                            Rental Tax Estimates for the year {selectedYear} (To be paid once a year)
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <Grid container spacing={3}>
                            {/* For Individuals */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                    border: "1px solid #e0e0e0", 
                                    borderRadius: "8px", 
                                    padding: "1.5rem",
                                    backgroundColor: "#fafafa"
                                }}>
                                    <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
                                        For Individuals
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold" color="success.main" mb={2}>
                                        {currencyFormatter(parseFloat(calculatedSole))}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        1. Get total income ({currencyFormatter(parseFloat(totalIncome))}), Subtract UGX 2,820,000
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        2. Taxable income on your rentals will be {currencyFormatter(parseFloat(taxableIncomeForIndividuals))}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        3. Get 12% of the taxable income from your rentals
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* For Businesses */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                    border: "1px solid #e0e0e0", 
                                    borderRadius: "8px", 
                                    padding: "1.5rem",
                                    backgroundColor: "#fafafa"
                                }}>
                                    <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
                                        For Businesses
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold" color="success.main" mb={2}>
                                        {currencyFormatter(parseFloat(calculatedPartnerships))}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        1. URA allows up to 50% of the annual gross rental income as expenses and losses incurred
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        2. Taxable income on your rentals will be {currencyFormatter(parseFloat(taxableIncomeForBusinesses))} (50% of total balance: {currencyFormatter(parseFloat(totalBalance))})
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        3. Get 30% of the taxable income from your rentals
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </>
    );
}
